import { useContext, useMemo } from 'react'
import { min } from 'lib/array/arrayUtils'
import { BundleOfferPackageOptionView, getAvailableBundlePackages } from 'selectors/offerPage/bundledOfferPageSelectors'
import { coverByOffersStates, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { getSelectedOfferPageDay } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'

interface BundleSelectedDaysInfo {
  duration: number,
  hotelTotal: number,
  available: boolean,
  taxesAndFees: number,
}

export default function useBundleSelectedDays(): BundleSelectedDaysInfo {
  const currentState = useContext(OfferPageStateContext)
  const appState = useAppSelector(appState => appState)
  const availableBundlePackages = getAvailableBundlePackages(currentState, appState)
  const selectedDays = coverByOffersStates<App.CalendarDay>(getSelectedOfferPageDay, currentState, appState)

  const result = useMemo(() => {
    const state = takeCurrentOfferState(currentState)
    const bundledOffers = state.bundleOffer ? Object.values(state.bundledOffers ?? {}) : []
    const isBundleOffer = bundledOffers.length > 0
    if (!isBundleOffer) {
      return {
        duration: 0,
        hotelTotal: 0,
        available: false,
        taxesAndFees: 0,
      }
    }

    const filteredBundlePackageId = currentState.filteredBundlePackageId
    let availableBundlePackage: BundleOfferPackageOptionView | undefined
    if (availableBundlePackages) {
      availableBundlePackage = filteredBundlePackageId ? availableBundlePackages?.[filteredBundlePackageId] : min(Object.values(availableBundlePackages), p => p.hotelPrice || Number.MAX_SAFE_INTEGER)
    }

    return (selectedDays || []).reduce((acc, item, index) => {
      const currentOffer = bundledOffers[index]
      const currentOfferOption = availableBundlePackage?.options?.find(o => o.package.offerId === currentOffer.offer.id)
      const hotelTotal = !item?.hotelTotal && currentOfferOption ? (currentOfferOption.hotelPrice + currentOfferOption.propertyFees) : (item?.hotelTotal ?? 0)

      return {
        duration: acc.duration + currentOffer.duration,
        hotelTotal: acc.hotelTotal + hotelTotal,
        available: true,
        taxesAndFees: acc.taxesAndFees + (currentOfferOption?.propertyFees ?? 0),
      }
    }, {
      duration: 0,
      hotelTotal: 0,
      available: false,
      taxesAndFees: 0,
    })
  }, [currentState, availableBundlePackages, selectedDays])

  return result
}
